<template lang="pug">
AppDialog.job-dialog(
  show
  has-footer
  title="Job"
  append-to-body
  :is-dismissable="dismissable"
  :class="{ 'put-under': !dismissable }"
  @close="$emit('close')"
)
  template(#titleLabel)
    small.job-status.label.ml-10(
      :class="jobStatus.class"
    )
      | {{ jobStatus.name }}
  AppTabs(compact)
    AppTab(
      :label="'General'"
      tab-id="general"
      @tab-selected="selectTab('general')"
    )
    AppTab(
      :label="'Visits'"
      tab-id="visits"
      @tab-selected="selectTab('visits')"
    )
  #show-job-general(v-if="isGeneralTab")
    table.wo-job-details.table
      tbody
        tr
          td Job ID
          td {{ job.summary }}
        tr
          td Work order
          td {{ workOrder.number }}
        tr
          td Client
          td {{ workOrder.client.name }}
        tr
          td Scheduled
          td
            .d-flex.align-items-baseline(v-if="hasJob")
              | {{ $filters.formatDateTime(job.startsAt, false) }} – {{ $filters.formatTime(job.stopsAt, false) }} {{ job.timeZoneAbbr }}
              template(v-if="isRecurringJob")
                .hi.hi-refresh.ml-5(
                  v-tooltip
                  :title="recurringScheduleTooltip"
                )
        tr
          td Address
          td {{ (workOrder.jobSite || {}).address }}
        tr
          td Assignees
          td
            template(v-if="hasJob")
              HubAvatar.avatar-overlaps(
                v-for="assignee in jobAssignees"
                :key="assignee.id"
                v-tooltip="{ delay: { hide: 0 } }"
                :users-avatars="usersAvatars"
                :title="assignee.name"
                :user-id="assignee.id"
                avatar-style="medium"
              )
              span.more-members-badge.light(v-if="showMoreBadge") +{{ job.assignees.length - 3 }}
        tr
          td Instructions
          td.work-order-instructions
            AppExcerpt(
              v-if="workOrder.instructions"
              :collapse-height="120"
            )
              p.break-word(
                v-for="(line, index) in instructionsLines"
                :key="index"
              )
                template(v-if="line.length") {{ line }}
                template(v-else) &nbsp;
        tr
          td Details
          td
            template(v-if="hasJob && job.details")
              AppExcerpt.wo-job-details-details(:collapse-height="120")
                .break-word {{ job.details }}

    .job-stats-table.d-flex.flex-sm-row.mb-20.mt-20(v-if="hasJob && hasStarted")
      .time-worked
        .small.text-grey TIME WORKED
        p.stat-value.mt-5 {{ $filters.formatDuration(job.timeWorked, false) }}

      .time-started.ml-20
        .small.text-grey TIME STARTED
        p.stat-value.mt-5
          template(v-if="job.actualStartTime") {{ $filters.formatTime(job.actualStartTime, false) }}
          template(v-else) –

      .time-stopped.ml-20
        .small.text-grey TIME STOPPED
        p.stat-value.mt-5
          template(v-if="job.actualStopTime") {{ $filters.formatTime(job.actualStopTime, false) }}
          template(v-else) –

    table#job-notes.wo-job-details.table(v-if="job.notes")
      tbody
        tr
          td Notes
          td
            template(v-if="job.notes.length")
              WorkOrdersJobNote(
                v-for="note in job.notes"
                :key="note.id"
                :note="note"
              )
            p.is-muted(v-else) This job doesn't have any notes

  #show-job-visits(v-if="isVisitsTab")
    .table-wrapper(:class="{ 'is-scrollable': showScrollableShadow }")
      .scrollable
        table.small.wo-job-visits.table
          thead
            tr
              th Member
              th Status
              th Date
              th Time started
              th Time stopped
          tbody
            tr(
              v-for="(visit, index) in jobVisits"
              :key="visit.assignee.id + '-' + index"
            )
              td
                HubAvatar(
                  :key="visit.assignee.id"
                  :user-id="visit.assignee.id"
                  :users-avatars="usersAvatars"
                  avatar-style="speck"
                )
                | {{ visit.assignee.text }}
              td
                small.shift-status.label.valign-middle(:class="visit.status.symbol")
                  | {{ visit.status.name }}
              td {{ $filters.formatDate(visit.date) }}
              td
                template(v-if="visit.startTime") {{ $filters.formatTime(visit.startTime, false) }}
                template(v-else) –
              td
                template(v-if="visit.stopTime") {{ $filters.formatTime(visit.stopTime, false) }}
                template(v-else) –
    a.job-reports-link.mb-15(
      v-if="workOrder.jobSite"
      :href="job.visitsReportUrl"
    )
      i.hi-document.mr-5
      | Job site visits report
  .job-dialog__footer.d-flex.justify-content-between(v-if="canEditJobs")
    AppButton.ml-0.mt-0(
      v-if="!isJobCompleted"
      type="success"
      :disabled="processingJob"
      @click="onJobCompleted"
    ) Mark as completed
    AppButton.ml-0.mt-0(
      v-else
      type="outline"
      :disabled="processingJob"
      @click="onJobReopened"
    ) Reopen
    AppButton.ml-md-10.mt-0(
      type="primary"
      @click="onJobEdit"
    ) Edit
</template>

<script>
import { RecurringJobs } from '~/mixins';
import { mapActions } from 'vuex';
import WorkOrdersJobNote from './WorkOrdersJobNote.vue';
import { humanizeRecurringSchedule } from '~/utils/recurring_schedule';
import WorkOrdersTemporaryLightGallery from './WorkOrdersTemporaryLightGallery';
import { createUsersAvatarsMixin } from '~/mixins/UsersAvatarsMixin';

export default {
  name: 'ShowJobDialog',
  components: {
    WorkOrdersJobNote,
  },
  mixins: [RecurringJobs, createUsersAvatarsMixin('job.assignees')],
  props: {
    processingJob: {
      type: Boolean,
      required: true,
    },
    show: {
      type: Boolean,
      default: false,
    },
    job: {
      type: Object,
      default: () => ({}),
    },
    workOrder: {
      type: Object,
      required: true,
    },
    canEditJobs: {
      type: Boolean,
      required: true,
    },
  },
  data: () => ({
    dismissable: true,
    selectedTab: 'general',
    showScrollableShadow: false,
  }),
  computed: {
    isGeneralTab() {
      return this.selectedTab === 'general';
    },
    isVisitsTab() {
      return this.selectedTab === 'visits';
    },
    isRecurringJob() {
      return this.jobIsRecurrent(this.job);
    },
    recurringScheduleTooltip() {
      return this.isRecurringJob ? humanizeRecurringSchedule(this.job.schedule) : null;
    },
    hasJob() {
      return !!Object.keys(this.job || {}).length;
    },
    jobVisits() {
      return !this.hasJob ? [] : this.job.visits;
    },
    jobStatus() {
      let name = '';
      let klass = '';

      if (this.hasJob) {
        const job = this.job;

        klass = `label-${job.status ? job.status.symbol : ''}`;
        name = job.status ? job.status.name : '';
      }

      return { class: klass, name };
    },
    jobAssignees() {
      return (this.job.assignees || []).slice(0, 3);
    },
    showMoreBadge() {
      return (this.job.assignees || []).length > 3;
    },
    hasStarted() {
      return this.hasJob && this.job.startsAt ? this.job.startsAt.isBefore(moment(), 'second') : false;
    },
    instructionsLines() {
      return (this.workOrder.instructions || '').split('\n');
    },
    isJobCompleted() {
      return this.job.status.name === 'Completed';
    },
  },
  async mounted() {
    this.showScrollableShadow = window.innerWidth < 414;
    // This is needed to make sure that the DOM has fully rendered and mounted
    // so that jQuery in this case has access to it. Otherwise, sometimes
    // the `lightGallery` wasn't being initialized properly and was instead
    // redirecting to the image URL.
    await this.$nextTick();
    // Right now there's a limitation with `lightGallery` in the sense
    // that we're limited to 1 instance per page. Which means that we cannot
    // have one for each note and its attachments.
    //
    // We could, with the plugin `lg-hash` but maybe it's worth waiting until
    // the vuetification of the gallery component and use that instead.
    WorkOrdersTemporaryLightGallery.initialize(this.$el, (open) => {
      // This is so that if the user hits 'ESC' we don't close the
      // the Gallery as well as the ShowJobDialog.
      this.dismissable = !open;
    });
  },
  beforeUnmount() {
    WorkOrdersTemporaryLightGallery.destroy();
  },
  methods: {
    ...mapActions(['setShowJobDialog']),
    selectTab(tab) {
      this.selectedTab = tab;
    },
    onJobEdit() {
      if (this.hasJob) {
        this.$emit('edit-job', this.job);
      }
    },
    onJobCompleted() {
      if (this.hasJob) {
        this.$emit('complete-job', this.job);
      }
    },
    onJobReopened() {
      if (this.hasJob) {
        this.$emit('reopen-job', this.job);
      }
    },
  },
};
</script>
